import { ReportFormat, SelectedFile } from './types';

/** What the generation flow starts from when Siesta's proposal was approved for review. */
export type SuitabilityFlowPrefill = {
  clientId: string | null;
  familyId: string | null;
  templateId: string;
  reportFormat: ReportFormat;
  adviceFiles: SelectedFile[];
  feeDocuments: SelectedFile[];
};

type ApprovedProposal = {
  clientId: string | null;
  familyId: string | null;
  templateId: string;
  reportFormat: ReportFormat;
  documents: { role: 'ADVICE' | 'FEE'; name: string; dataUploadFileId?: string | null }[];
};

type SuitabilityFlowLink = {
  to: string;
  state: { fromPath: string; familyId?: string; prefill: SuitabilityFlowPrefill };
};

// The numeric File id is only read for appendices, so -1 matches useItemSelectionMapping's
// placeholder for a file it has no id for.
const toSelectedFiles = (
  documents: ApprovedProposal['documents'],
  role: 'ADVICE' | 'FEE',
): SelectedFile[] =>
  documents.flatMap(document =>
    document.role === role && document.dataUploadFileId
      ? [
          {
            id: document.dataUploadFileId,
            name: document.name,
            fileId: -1,
            dataUploadFileId: document.dataUploadFileId,
          },
        ]
      : [],
  );

/**
 * The flow lives under a client URL even for a household, so a household proposal is opened
 * under a host client: the one in the route, else the household page's `member` choice, else the
 * household's main member, as the household documents page picks it.
 */
export function resolveSuitabilityHostClientId({
  routeClientId,
  memberParam,
  familyMainClientId,
  familyMemberIds,
}: {
  routeClientId: string | null;
  memberParam: string | null;
  familyMainClientId: string | null;
  /** Null until the household has loaded; a `member` choice is then taken as given. */
  familyMemberIds?: string[] | null;
}): string | null {
  const memberIsInHousehold =
    !familyMemberIds || (memberParam && familyMemberIds.includes(memberParam));
  return routeClientId ?? (memberIsInHousehold ? memberParam : null) ?? familyMainClientId;
}

/** Whether approving this proposal can be followed by opening its flow. */
export function canOpenSuitabilityFlow(
  owner: { clientId?: string | null },
  hostClientId: string | null,
): boolean {
  return Boolean(owner.clientId ?? hostClientId);
}

export function suitabilityFlowLink(
  proposal: ApprovedProposal,
  hostClientId: string | null,
  fromPath: string,
): SuitabilityFlowLink | null {
  const clientId = proposal.clientId ?? hostClientId;
  if (!clientId) return null;
  return {
    to: `/client/${clientId}/suitability-report`,
    state: {
      fromPath,
      ...(proposal.familyId ? { familyId: proposal.familyId } : {}),
      prefill: {
        clientId: proposal.clientId,
        familyId: proposal.familyId,
        templateId: proposal.templateId,
        reportFormat: proposal.reportFormat,
        adviceFiles: toSelectedFiles(proposal.documents, 'ADVICE'),
        feeDocuments: toSelectedFiles(proposal.documents, 'FEE'),
      },
    },
  };
}
